/**
 * API helpers for the self-service zone (Keycloak group) picker.
 *
 * Every zone requires an administrator's approval before membership is
 * granted -- there is no instant-join tier. Requesting access only sends a
 * notification; leaving a zone is self-service and needs no approval since
 * it only ever reduces the caller's own access.
 *
 * Mirrors the CSRF pattern used in utils/egressAuth.ts.
 */
import axios from 'axios';

export interface ZoneSummary {
  name: string;
  description: string | null;
  is_member: boolean;
  has_pending_request: boolean;
}

async function csrfHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  try {
    const resp = await axios.get('/api/auth/csrf-token');
    const token = resp.data?.csrf_token;
    if (token) headers['X-CSRF-Token'] = token;
  } catch {
    // No CSRF token (e.g. bearer auth) — the backend dependency is flexible.
  }
  return headers;
}

/** List all zones, with is_member reflecting the current user's own membership. */
export async function listZones(): Promise<ZoneSummary[]> {
  const resp = await axios.get('/api/zones');
  return (resp.data?.zones ?? []) as ZoneSummary[];
}

/** Request access to a zone. Sends an admin a notification; grants nothing directly. */
export async function requestZoneAccess(zoneName: string, reason?: string): Promise<void> {
  const headers = await csrfHeaders();
  await axios.post(
    `/api/zones/${encodeURIComponent(zoneName)}/request-access`,
    { reason: reason?.trim() || null },
    { headers }
  );
}

/** Leave a zone the caller currently belongs to. No approval required. */
export async function leaveZone(zoneName: string): Promise<void> {
  const headers = await csrfHeaders();
  await axios.delete(`/api/zones/${encodeURIComponent(zoneName)}/leave`, { headers });
}
